import React, { useEffect, useState } from "react";
import { Tabs, Row, Col } from "antd";
import { useHistory } from "react-router-dom";
import {
  CheckCircleFilled,
  CloudServerOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";

import { useAuth } from "../../hooks/useAuth";
import LoginForm from "./LoginForm";
import SignupForm from "./SignupForm";
import KineticaLogo from "../../images/logo.svg";
import { STORAGE_KEY_REDIRECT } from "../../constants";
import tokens from "../../styles/tokens";
import AuroraBackground from "../Layout/AuroraBackground";
import "./Trynow.less";

const { TabPane } = Tabs;

const FREE_COLORS = [tokens.brand[500], tokens.brand[300]];
const DEDICATED_COLORS = [tokens.accent[500], tokens.accent[400]];

const freeFeatures = [
  "Up to 10GB of data storage",
  "Shared compute resources",
  "Full database functionality",
  "Unlimited SQL & API access",
  "Unlimited SQL workbooks",
  "Basic Support",
];

const dedicatedFeatures = [
  "No data limits",
  "Dedicated compute resources",
  "Full database functionality",
  "Dashboarding with Reveal",
  "Pay-as-you-go hourly pricing",
  "Basic Support",
];

const PitchSection = ({ tier, colors, icon, label, title, description, features }) => (
  <div>
    <div
      className={`trynow-icon-block trynow-icon-block--${tier}`}
      style={{
        height: 44,
        width: 44,
        padding: 8,
        borderRadius: 10,
        marginTop: 2,
        marginRight: 14,
        marginBottom: 8,
        float: "left",
        background: `linear-gradient(135deg, ${colors[0]} 0%, ${colors[1]} 100%)`,
      }}
    >
      {icon}
    </div>
    <div style={{ overflow: "hidden" }}>
      <label
        className="trynow-tier-label"
        style={{
          fontSize: 12,
          lineHeight: "14px",
        }}
      >
        {label}
      </label>
      <h2
        style={{
          fontSize: 20,
          lineHeight: "26px",
          fontWeight: 600,
          margin: "2px 0 0 0",
          color: tokens.surface[950],
        }}
      >
        {title}
      </h2>
    </div>
    <p
      style={{
        clear: "both",
        margin: "10px 0 8px",
        color: tokens.surface[900],
      }}
    >
      {description}
    </p>
    <Row gutter={[8, 0]}>
      {features.map((feature, idx) => (
        <Col xs={24} sm={12} key={idx}>
          <div
            className="trynow-feature"
            style={{
              display: "flex",
              alignItems: "flex-start",
              padding: "3px 0",
            }}
          >
            <CheckCircleFilled
              style={{
                fontSize: 16,
                color: colors[0],
                marginTop: 4,
                flexShrink: 0,
              }}
            />
            <span style={{ marginLeft: 6, lineHeight: "20px" }}>{feature}</span>
          </div>
        </Col>
      ))}
    </Row>
  </div>
);

const Trynow = () => {
  const [activeTabKey, setActiveTabKey] = useState("signup");
  const auth = useAuth();
  const history = useHistory();

  useEffect(
    (_) => {
      if (auth.user) {
        history.push("/");
      } else {
        sessionStorage.setItem(STORAGE_KEY_REDIRECT, "/free");
      }
    },
    [auth, history]
  );

  const tabStyle = { width: "125px", textAlign: "center" };
  const wrapperStyle = {
    position: "relative",
    zIndex: 1,
    width: "100%",
    maxWidth: 960,
  };

  return (
    <AuroraBackground>
      <div className="trynow-page">
        <div style={wrapperStyle}>
        <div
          className="trynow-header"
          style={{ textAlign: "center", marginBottom: "20px" }}
        >
          <img
            src={KineticaLogo}
            alt="Kinetica Logo"
            style={{ height: "40px", position: "relative", top: "-3px" }}
          />
          <h1
            style={{
              color: tokens.semantic.warning,
              fontSize: "34px",
              fontWeight: "200",
              letterSpacing: "-0.025em",
            }}
          >
            {process.env.REACT_APP_SITE_TITLE}
          </h1>
        </div>
        <div
          className="login trynow-card"
          style={{
            padding: "24px 60px 32px",
            borderRadius: "20px",
          }}
        >
        <Row gutter={50}>
          <Col xs={24} md={14}>
            <div style={{ padding: "10px 0 0 0" }}>
              <PitchSection
                tier="free"
                colors={FREE_COLORS}
                icon={
                  <ExperimentOutlined
                    style={{ fontSize: 26, color: tokens.surface[950] }}
                  />
                }
                label="FOR GETTING STARTED"
                title="Create your Free Kinetica Cloud Account"
                description="Sign up now to create your free account, no credit card required. You will get instant access to Kinetica Cloud and your very own Database for Time and Space. Free. Forever."
                features={freeFeatures}
              />
              <div className="trynow-divider" />
              <PitchSection
                tier="dedicated"
                colors={DEDICATED_COLORS}
                icon={
                  <CloudServerOutlined
                    style={{ fontSize: 26, color: tokens.surface[950] }}
                  />
                }
                label="FOR DEDICATED WORKLOADS"
                title="Need More? Try Kinetica Cloud Dedicated"
                description="For production use-cases with demanding workloads. Get dedicated compute resources, no data limits, and pay-as-you-go hourly pricing — only pay for what you use."
                features={dedicatedFeatures}
              />
              <p className="trynow-closing">
                <strong>Sign up free</strong> — you can upgrade to a dedicated
                cluster anytime from your dashboard.
              </p>
            </div>
          </Col>
          <Col xs={24} md={10}>
            <div style={{ paddingLeft: 32 }}>
              <Tabs
                activeKey={activeTabKey}
                size="large"
                tabBarGutter={0}
                onChange={(active) => setActiveTabKey(active)}
                destroyInactiveTabPane
                centered
              >
                <TabPane tab={<div style={tabStyle}>Sign Up</div>} key="signup">
                  <div style={{ padding: "10px 0px" }}>
                    <SignupForm setActiveTabKey={setActiveTabKey} />
                  </div>
                </TabPane>
                <TabPane tab={<div style={tabStyle}>Login</div>} key="login">
                  <div style={{ padding: "10px 0px" }}>
                    <LoginForm setActiveTabKey={setActiveTabKey} />
                  </div>
                </TabPane>
              </Tabs>
            </div>
          </Col>
        </Row>
        </div>
      </div>
      </div>
    </AuroraBackground>
  );
};

export default Trynow;
